/**
 * 알림톡 템플릿 에디터 (알리고 카카오 비즈메시지 API 스펙 기반)
 *
 * - tpl_code 는 알리고 검수 승인 후 자동 부여되는 식별자이므로 사용자가 직접 입력하지 않는다.
 * - 사용자는 tpl_name(템플릿 이름), senderkey(발신프로필 키), tpl_content(본문) 등 알리고가
 *   요구하는 등록 파라미터를 입력하고, 저장 후 알리고 API(/akv10/template/add/)로 전송하는 방식.
 * - 이 에디터는 입력값을 알리고 스펙 그대로의 필드명으로 저장한다(추후 백엔드에서 그대로 전송 가능).
 *
 * 알리고 카카오 알림톡 템플릿 등록 파라미터 매핑:
 *   tpl_name              템플릿 이름 (사용자 입력)
 *   senderkey             발신프로필 키 (Aligo 콘솔에서 발급)
 *   category_code         카테고리 코드 (Aligo 카테고리 목록 중 선택)
 *   tpl_message_type      메시지 유형 BA/EX/AD/MI
 *   tpl_emphasize_type    강조 유형 NONE/TEXT/IMAGE/ITEM_LIST
 *   tpl_emphasize_title   강조 제목
 *   tpl_emphasize_subtitle 강조 부제목
 *   tpl_image_url         강조 이미지 URL
 *   tpl_content           본문 내용 (#{변수} 사용)
 *   tpl_extra             부가 정보 (EX/MI)
 *   tpl_ad                광고 문구 (AD/MI)
 *   tpl_button{N}_*       버튼 (최대 5개) — type/name/url_mobile/url_pc/scheme_android/scheme_ios
 *
 * 응답에서 받는 값(저장 후 갱신):
 *   tpl_code              알리고가 부여한 템플릿 코드
 *   status                R 등록 / A 승인대기 / S 승인 / F 반려
 */
import React, {Component} from 'react';
import { observable, action, toJS } from 'mobx';
import { observer, inject } from 'mobx-react';
import { toast } from 'react-toastify';
import PageTitle from "../../../components/common/PageTitle";
import FeatherIcon from 'feather-icons-react';
import {
  Button, Card, Row, Col,
  FormInput, FormSelect, FormTextarea
} from "shards-react";

/* ------------ 알리고 카카오 API 상수 ------------ */
const MSG_TYPES = [
  { value: 'BA', label: 'BA · 기본형' },
  { value: 'EX', label: 'EX · 부가정보형' },
  { value: 'AD', label: 'AD · 광고추가형' },
  { value: 'MI', label: 'MI · 복합형' }
];

const EMPHASIZE_TYPES = [
  { value: 'NONE',      label: '없음' },
  { value: 'TEXT',      label: '제목/부제목 강조' },
  { value: 'IMAGE',     label: '이미지 강조' },
  { value: 'ITEM_LIST', label: '아이템 리스트' }
];

// 알리고 버튼 유형 코드
const BUTTON_TYPES = [
  { value: 'WL', label: '웹링크 (WL)' },
  { value: 'AL', label: '앱링크 (AL)' },
  { value: 'BK', label: '봇 키워드 (BK)' },
  { value: 'MD', label: '메시지 전달 (MD)' },
  { value: 'DS', label: '배송 조회 (DS)' },
  { value: 'BC', label: '상담톡 전환 (BC)' }
];

const STATUS_LABEL = {
  unregistered: { label: '미등록',     color: '#6B7280', bg: '#F3F4F6' },
  R:            { label: '등록',       color: '#374151', bg: '#F3F4F6' },
  A:            { label: '승인 대기',  color: '#92400E', bg: '#FEF3C7' },
  S:            { label: '승인',       color: '#065F46', bg: '#D1FAE5' },
  F:            { label: '반려',       color: '#991B1B', bg: '#FEE2E2' }
};

/* ------------ 스타일 헬퍼 ------------ */
const sectionTitleStyle = {
  fontSize: 13, fontWeight: 700, color: '#1F2937',
  marginTop: 22, marginBottom: 10,
  display: 'flex', alignItems: 'center', gap: 8
};
const labelStyle = {
  fontSize: 12, fontWeight: 600, color: '#374151',
  marginBottom: 4, display: 'block'
};
const subStyle = { fontSize: 11, color: '#6B7280', fontWeight: 400 };

/* ------------ 변수 칩 ------------ */
const VarChip = ({name}) => (
  <span style={{
    display:'inline-block', padding:'2px 8px',
    background:'#DBEAFE', color:'#1E40AF',
    borderRadius:4, marginRight:4, marginBottom:4,
    fontSize:11, fontWeight:700
  }}>#{`{${name}}`}</span>
);

/* ------------ 미리보기 (실제 알림톡 메시지 UI) ------------ */
const KakaoPreview = ({form, contentHtml}) => {
  const f = form;
  return (
    <div style={{
      borderRadius: 14, overflow: 'hidden',
      background: '#A0C3D9', padding: 12
    }}>
      <div style={{background:'#fff', borderRadius: 10, padding: 14, fontSize: 13, lineHeight: 1.7, color: '#1F2937'}}>
        {/* 알림톡 라벨 */}
        <div style={{display:'flex', alignItems:'center', gap:6, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #F3F4F6'}}>
          <div style={{
            width:22, height:22, background:'#FEE500', borderRadius:'50%',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:11, fontWeight:800, color:'#3C1E1E'
          }}>K</div>
          <span style={{fontWeight:700, fontSize:12, color:'#1F2937'}}>알림톡 도착</span>
          {f.tpl_message_type && (
            <span style={{marginLeft:'auto', fontSize:10, padding:'2px 6px', background:'#F3F4F6', color:'#6B7280', borderRadius:4, fontWeight:600}}>{f.tpl_message_type}</span>
          )}
        </div>

        {/* 강조 — TEXT */}
        {f.tpl_emphasize_type === 'TEXT' && (f.tpl_emphasize_title || f.tpl_emphasize_subtitle) && (
          <div style={{marginBottom: 12}}>
            {f.tpl_emphasize_subtitle && <div style={{fontSize:11, color:'#6B7280', marginBottom: 2}}>{f.tpl_emphasize_subtitle}</div>}
            {f.tpl_emphasize_title && <div style={{fontSize:18, fontWeight:800, color:'#1F2937', lineHeight:1.3}}>{f.tpl_emphasize_title}</div>}
          </div>
        )}
        {/* 강조 — IMAGE */}
        {f.tpl_emphasize_type === 'IMAGE' && f.tpl_image_url && (
          <img src={f.tpl_image_url} alt="" style={{width:'100%', borderRadius:6, marginBottom:12, display:'block'}}/>
        )}

        {/* 본문 */}
        <div style={{whiteSpace:'pre-wrap', wordBreak:'break-word'}}>
          {contentHtml ? <span dangerouslySetInnerHTML={{__html: contentHtml}}/> :
            <span style={{color:'#9CA3AF'}}>본문(tpl_content)을 입력하세요.</span>}
        </div>

        {/* 부가정보 */}
        {f.tpl_extra && (f.tpl_message_type === 'EX' || f.tpl_message_type === 'MI') && (
          <div style={{marginTop:12, paddingTop:10, borderTop:'1px dashed #E5E7EB', fontSize:12, color:'#6B7280', whiteSpace:'pre-wrap'}}>
            {f.tpl_extra}
          </div>
        )}

        {/* 광고 */}
        {f.tpl_ad && (f.tpl_message_type === 'AD' || f.tpl_message_type === 'MI') && (
          <div style={{marginTop:10, padding:'6px 8px', background:'#F9FAFB', borderRadius:4, fontSize:11, color:'#6B7280', whiteSpace:'pre-wrap'}}>
            (광고) {f.tpl_ad}
          </div>
        )}

        {/* 버튼 */}
        {f.buttons && f.buttons.length > 0 && (
          <div style={{marginTop:12, display:'flex', flexDirection:'column', gap:6}}>
            {f.buttons.map((b, i) => (
              <button key={i} type="button" style={{
                padding:'9px 10px',
                border:'1px solid #E5E7EB', background:'#F9FAFB',
                borderRadius:6, fontSize:13, color:'#1F2937',
                cursor:'default', textAlign:'center', fontWeight:600
              }}>
                {b.button_name || '(버튼 이름)'}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------ 메인 컴포넌트 ------------ */
@inject(({ AOA }) => ({
  saveNotificationTemplate: AOA.saveNotificationTemplate,
  getAligoProfileList: AOA.getAligoProfileList
}))
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@observer
class NotificationKakaoTemplate extends Component {

  @observable form = this.emptyForm();
  @observable isSaving = false;

  // 등록된 카카오 채널 목록
  @observable channels = [];
  @observable channelsLoading = false;
  @observable channelsError = '';

  componentDidMount() {
    this.loadChannels();
  }

  @action loadChannels = async () => {
    this.channelsLoading = true;
    this.channelsError = '';
    try {
      const res = await this.props.getAligoProfileList();
      if (res && (res.list || res.code === 0)) {
        this.channels = res.list || [];
        if (res.code !== undefined && res.code !== 0 && (!res.list || res.list.length === 0)) {
          this.channelsError = res.message || '채널 목록 조회 실패';
        }
      } else if (res && res.message) {
        this.channelsError = res.message;
        this.channels = [];
      } else {
        this.channelsError = '채널 목록 응답이 비어 있습니다.';
        this.channels = [];
      }
    } catch (e) {
      this.channelsError = '채널 목록 조회 중 오류가 발생했습니다.';
      this.channels = [];
    }
    this.channelsLoading = false;
  };

  // 발신프로필 (고정) — 알리고 카카오 채널 @onreple
  SENDERKEY = 'ec6f089c22036d90a23abd5fa8cd362639ba9b78';
  SENDERKEY_ALIAS = '@onreple';

  // 알리고 API 스펙에 맞춘 필드 구조
  emptyForm() {
    return {
      // 사용자 입력 필드
      tpl_name: '',                  // 템플릿 이름 (알리고 식별용)
      senderkey: 'ec6f089c22036d90a23abd5fa8cd362639ba9b78',  // 고정 (@onreple)
      senderkey_alias: '@onreple',                            // 고정 채널명
      tpl_message_type: 'BA',        // BA / EX / AD / MI
      tpl_emphasize_type: 'NONE',    // NONE / TEXT / IMAGE / ITEM_LIST
      tpl_emphasize_title: '',
      tpl_emphasize_subtitle: '',
      tpl_image_url: '',
      tpl_content: '',               // 본문 (#{변수} 사용)
      tpl_extra: '',                 // 부가정보 (EX/MI)
      tpl_ad: '',                    // 광고문구 (AD/MI)
      buttons: [],                   // [{ button_type, button_name, url_mobile, url_pc, scheme_android, scheme_ios }]
      memo: '',

      // 알리고 응답으로 채워지는 필드 (등록 후 갱신)
      tpl_code: '',                  // 알리고 자동 부여 (등록 후 응답)
      status: 'unregistered'         // unregistered / R / A / S / F
    };
  }

  /* 본문에서 #{변수} 추출 (중복 제거) */
  detectVariables = () => {
    const re = /#\{([^}]+)\}/g;
    const text = (this.form.tpl_content || '') + ' '
               + (this.form.tpl_emphasize_title || '') + ' '
               + (this.form.tpl_emphasize_subtitle || '');
    const set = {};
    let m;
    while ((m = re.exec(text)) !== null) {
      set[m[1].trim()] = true;
    }
    return Object.keys(set);
  };

  /* 본문 미리보기 HTML (변수 강조 + 개행) */
  contentHtml = () => {
    const content = (this.form.tpl_content || '');
    if (!content) return '';
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return esc(content)
      .replace(/#\{([^}]+)\}/g, '<span style="background:#DBEAFE;color:#1E40AF;padding:0 4px;border-radius:3px;font-weight:700;">#{$1}</span>')
      .replace(/\n/g, '<br/>');
  };

  @action onChange = (key, value) => {
    this.form[key] = value;
  };

  @action onChangeButton = (idx, key, value) => {
    if (this.form.buttons[idx]) {
      this.form.buttons[idx][key] = value;
    }
  };

  @action addButton = () => {
    if (this.form.buttons.length >= 5) {
      toast.error('버튼은 최대 5개까지 추가할 수 있습니다.');
      return;
    }
    this.form.buttons.push({
      button_type: 'WL',
      button_name: '',
      url_mobile: '',
      url_pc: '',
      scheme_android: '',
      scheme_ios: ''
    });
  };

  @action removeButton = (idx) => {
    this.form.buttons.splice(idx, 1);
  };

  @action moveButton = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= this.form.buttons.length) return;
    const arr = this.form.buttons;
    const tmp = arr[idx]; arr[idx] = arr[newIdx]; arr[newIdx] = tmp;
  };

  goBack = () => {
    if (this.props.history) this.props.history.push('/notificationSend');
    else window.location.href = '/notificationSend';
  };

  @action save = async () => {
    const f = this.form;
    if (!f.tpl_name || !f.tpl_name.trim())    { toast.error('템플릿 이름(tpl_name)을 입력해주세요.'); return; }
    if (!f.senderkey || !f.senderkey.trim())  { toast.error('발신프로필 키(senderkey)를 입력해주세요.'); return; }
    if (!f.tpl_content || !f.tpl_content.trim()) { toast.error('본문(tpl_content)을 입력해주세요.'); return; }
    if (f.tpl_emphasize_type === 'TEXT' && !(f.tpl_emphasize_title || '').trim()) {
      toast.error('강조 제목을 입력해주세요.'); return;
    }
    if (f.tpl_emphasize_type === 'IMAGE' && !(f.tpl_image_url || '').trim()) {
      toast.error('강조 이미지 URL을 입력해주세요.'); return;
    }

    this.isSaving = true;
    try {
      const data = {
        kind: 'kakao_template',
        ...toJS(f),
        variables: this.detectVariables()
      };
      const res = await this.props.saveNotificationTemplate({ data });
      if (res && res.errors && res.errors.length > 0) {
        toast.error((res.errors[0] && res.errors[0].message) || '저장에 실패했습니다.');
      } else {
        toast.success('알림톡 템플릿이 저장되었습니다. (tpl_code 는 알리고 검수 후 부여됩니다)');
        this.goBack();
      }
    } catch (e) {
      toast.error('저장 중 오류가 발생했습니다.');
    }
    this.isSaving = false;
  };

  render() {
    const f = this.form;
    const vars = this.detectVariables();
    const hasExtra = (f.tpl_message_type === 'EX' || f.tpl_message_type === 'MI');
    const hasAd    = (f.tpl_message_type === 'AD' || f.tpl_message_type === 'MI');
    const status = STATUS_LABEL[f.status] || STATUS_LABEL.unregistered;

    return (
      <div style={{padding: '0 16px 80px'}}>
        {/* 상단 바 */}
        <div style={{display:'flex', alignItems:'center', gap:8, marginTop: 8, marginBottom: 12}}>
          <Button theme="light" size="sm" onClick={this.goBack} style={{display:'inline-flex', alignItems:'center', gap:4}}>
            <FeatherIcon icon="arrow-left" size="14"/> 알림 발송
          </Button>
        </div>

        <PageTitle title="새 알림톡 템플릿" subtitle="알리고 카카오 API 스펙" className="mb-3"/>

        <Row>
          {/* ===== 좌: 폼 ===== */}
          <Col lg="7" md="12" style={{marginBottom: 16}}>
            <Card small style={{padding: '20px 24px'}}>

              {/* 등록 상태 안내 */}
              <div style={{padding:'10px 12px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom: 12, fontSize:12, color:'#92400E', display:'flex', alignItems:'flex-start', gap:8}}>
                <FeatherIcon icon="info" size="14" style={{flexShrink:0, marginTop:1}}/>
                <div>
                  <b>tpl_code(템플릿 코드)</b>는 알리고에 등록 후 검수 통과 시 <b>자동 부여</b>되는 식별자입니다. 여기서는 입력하지 않습니다.
                  &nbsp;현재 등록 상태: <span style={{padding:'1px 8px', borderRadius:4, background:status.bg, color:status.color, fontWeight:700, marginLeft:4}}>{status.label}</span>
                </div>
              </div>

              {/* --- 기본 정보 --- */}
              <div style={sectionTitleStyle}>기본 정보</div>
              <Row>
                <Col md="12" style={{marginBottom: 12}}>
                  <label style={labelStyle}>발신프로필 <span style={subStyle}>(senderkey · 고정)</span></label>
                  <div style={{
                    display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
                    padding:'10px 12px', background:'#F9FAFB',
                    border:'1px solid #E5E7EB', borderRadius:6
                  }}>
                    <span style={{
                      display:'inline-flex', alignItems:'center', gap:6,
                      padding:'4px 10px', background:'#FEF3C7', color:'#92400E',
                      borderRadius:4, fontSize:12, fontWeight:700
                    }}>
                      <FeatherIcon icon="message-circle" size="12"/> {this.SENDERKEY_ALIAS}
                    </span>
                    <code style={{
                      fontSize:11, color:'#6B7280', wordBreak:'break-all',
                      fontFamily:'Menlo, Consolas, monospace'
                    }}>
                      {f.senderkey}
                    </code>
                    <span style={{marginLeft:'auto', fontSize:11, color:'#9CA3AF'}}>수정 불가</span>
                  </div>
                </Col>
                <Col md="6" style={{marginBottom: 12}}>
                  <label style={labelStyle}>템플릿 이름 <span style={subStyle}>(tpl_name) *</span></label>
                  <FormInput value={f.tpl_name} onChange={e => this.onChange('tpl_name', e.target.value)} placeholder="예: 주문 확인"/>
                </Col>
                <Col md="6" style={{marginBottom: 12}}>
                  <label style={labelStyle}>메시지 유형 <span style={subStyle}>(tpl_message_type)</span></label>
                  <FormSelect value={f.tpl_message_type} onChange={e => this.onChange('tpl_message_type', e.target.value)}>
                    {MSG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </FormSelect>
                </Col>
              </Row>

              {/* --- 강조 표기 --- */}
              <div style={sectionTitleStyle}>강조 표기 <span style={subStyle}>(tpl_emphasize_*)</span></div>
              <Row>
                <Col md="12" style={{marginBottom: 12}}>
                  <label style={labelStyle}>강조 유형 <span style={subStyle}>(tpl_emphasize_type)</span></label>
                  <FormSelect value={f.tpl_emphasize_type} onChange={e => this.onChange('tpl_emphasize_type', e.target.value)}>
                    {EMPHASIZE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </FormSelect>
                </Col>
                {f.tpl_emphasize_type === 'TEXT' && (
                  <>
                    <Col md="6" style={{marginBottom: 12}}>
                      <label style={labelStyle}>강조 부제목 <span style={subStyle}>(tpl_emphasize_subtitle)</span></label>
                      <FormInput value={f.tpl_emphasize_subtitle} onChange={e => this.onChange('tpl_emphasize_subtitle', e.target.value)} placeholder="예: 주문번호"/>
                    </Col>
                    <Col md="6" style={{marginBottom: 12}}>
                      <label style={labelStyle}>강조 제목 <span style={subStyle}>(tpl_emphasize_title)</span></label>
                      <FormInput value={f.tpl_emphasize_title} onChange={e => this.onChange('tpl_emphasize_title', e.target.value)} placeholder="예: #{주문번호}"/>
                    </Col>
                  </>
                )}
                {f.tpl_emphasize_type === 'IMAGE' && (
                  <Col md="12" style={{marginBottom: 12}}>
                    <label style={labelStyle}>강조 이미지 URL <span style={subStyle}>(tpl_image_url)</span></label>
                    <FormInput value={f.tpl_image_url} onChange={e => this.onChange('tpl_image_url', e.target.value)} placeholder="https://..."/>
                  </Col>
                )}
              </Row>

              {/* --- 본문 --- */}
              <div style={sectionTitleStyle}>
                본문 <span style={subStyle}>(tpl_content) *</span>
                <span style={{...subStyle, marginLeft:'auto'}}>{`#{변수명} 형식 지원`}</span>
              </div>
              <FormTextarea
                value={f.tpl_content}
                onChange={e => this.onChange('tpl_content', e.target.value)}
                placeholder={`안녕하세요, #{고객명}님.\n주문 #{주문번호}이 정상 접수되었습니다.\n감사합니다.`}
                rows={9}
                style={{fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit'}}
              />
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:6, fontSize:11, color:'#9CA3AF'}}>
                <span>{(f.tpl_content || '').length}자 / 1000자</span>
                {vars.length > 0 && <span>감지된 변수 {vars.length}개</span>}
              </div>
              {vars.length > 0 && (
                <div style={{marginTop:8, padding:'8px 10px', background:'#F9FAFB', border:'1px solid #F3F4F6', borderRadius:6}}>
                  {vars.map(v => <VarChip key={v} name={v}/>)}
                </div>
              )}

              {/* --- 부가 정보 (EX/MI) --- */}
              {hasExtra && (
                <>
                  <div style={sectionTitleStyle}>부가 정보 <span style={subStyle}>(tpl_extra · 본문 하단)</span></div>
                  <FormTextarea value={f.tpl_extra} onChange={e => this.onChange('tpl_extra', e.target.value)} rows={3} placeholder="배송조회, 약관 안내 등 부가 정보"/>
                </>
              )}

              {/* --- 광고 문구 (AD/MI) --- */}
              {hasAd && (
                <>
                  <div style={sectionTitleStyle}>광고 문구 <span style={subStyle}>(tpl_ad · 광고성 정보)</span></div>
                  <FormTextarea value={f.tpl_ad} onChange={e => this.onChange('tpl_ad', e.target.value)} rows={3} placeholder="광고성 정보 표시 문구 / 수신거부 안내 등"/>
                </>
              )}

              {/* --- 버튼 --- */}
              <div style={sectionTitleStyle}>
                버튼 <span style={subStyle}>(tpl_button1~5 · 최대 5개)</span>
                <Button theme="info" size="sm" outline onClick={this.addButton} style={{marginLeft: 'auto', padding: '2px 10px', fontSize: 11, display:'inline-flex', alignItems:'center', gap:4}}>
                  <FeatherIcon icon="plus" size="11"/> 버튼 추가
                </Button>
              </div>
              {f.buttons.length === 0 ? (
                <div style={{fontSize:12, color:'#9CA3AF', padding:'10px 0'}}>등록된 버튼이 없습니다.</div>
              ) : (
                f.buttons.map((b, i) => (
                  <div key={i} style={{
                    display:'grid', gridTemplateColumns:'150px 1fr auto',
                    gap:8, padding:10, marginBottom:8,
                    background:'#F9FAFB', border:'1px solid #F3F4F6', borderRadius:8,
                    alignItems:'flex-start'
                  }}>
                    <div>
                      <label style={{...labelStyle, marginBottom: 4}}>유형 <span style={subStyle}>(button_type)</span></label>
                      <FormSelect value={b.button_type} onChange={e => this.onChangeButton(i, 'button_type', e.target.value)} style={{fontSize:12}}>
                        {BUTTON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </FormSelect>
                    </div>
                    <div>
                      <label style={{...labelStyle, marginBottom: 4}}>버튼 이름 <span style={subStyle}>(button_name)</span></label>
                      <FormInput value={b.button_name} onChange={e => this.onChangeButton(i, 'button_name', e.target.value)} placeholder="버튼에 표시될 문구" style={{fontSize:12}}/>
                      {b.button_type === 'WL' && (
                        <div style={{display:'flex', flexDirection:'column', gap:4, marginTop:6}}>
                          <FormInput value={b.url_mobile || ''} onChange={e => this.onChangeButton(i, 'url_mobile', e.target.value)} placeholder="모바일 URL (url_mobile)" style={{fontSize:12}}/>
                          <FormInput value={b.url_pc || ''} onChange={e => this.onChangeButton(i, 'url_pc', e.target.value)} placeholder="PC URL (url_pc · 선택)" style={{fontSize:12}}/>
                        </div>
                      )}
                      {b.button_type === 'AL' && (
                        <div style={{display:'flex', flexDirection:'column', gap:4, marginTop:6}}>
                          <FormInput value={b.scheme_android || ''} onChange={e => this.onChangeButton(i, 'scheme_android', e.target.value)} placeholder="Android 스킴 (scheme_android)" style={{fontSize:12}}/>
                          <FormInput value={b.scheme_ios || ''} onChange={e => this.onChangeButton(i, 'scheme_ios', e.target.value)} placeholder="iOS 스킴 (scheme_ios)" style={{fontSize:12}}/>
                        </div>
                      )}
                    </div>
                    <div style={{display:'flex', flexDirection:'column', gap:3}}>
                      <Button theme="light" size="sm" onClick={() => this.moveButton(i, -1)} style={{padding:'2px 5px'}} title="위로">
                        <FeatherIcon icon="chevron-up" size="11"/>
                      </Button>
                      <Button theme="light" size="sm" onClick={() => this.moveButton(i, 1)} style={{padding:'2px 5px'}} title="아래로">
                        <FeatherIcon icon="chevron-down" size="11"/>
                      </Button>
                      <Button theme="danger" size="sm" outline onClick={() => this.removeButton(i)} style={{padding:'2px 5px'}} title="삭제">
                        <FeatherIcon icon="x" size="11"/>
                      </Button>
                    </div>
                  </div>
                ))
              )}

              {/* --- 메모 --- */}
              <div style={sectionTitleStyle}>메모 <span style={subStyle}>(내부용 · 알리고에 전송되지 않음)</span></div>
              <FormTextarea value={f.memo} onChange={e => this.onChange('memo', e.target.value)} rows={2} placeholder="검수 메모, 사용처 등 내부 기록"/>

            </Card>
          </Col>

          {/* ===== 우: 미리보기 + 저장 ===== */}
          <Col lg="5" md="12">
            <Card small style={{padding: '20px 22px', position:'sticky', top: 16}}>
              <div style={{...sectionTitleStyle, marginTop: 0}}>
                <FeatherIcon icon="eye" size="14"/> 미리보기
              </div>
              <KakaoPreview form={f} contentHtml={this.contentHtml()}/>

              <div style={{marginTop:14, padding:'10px 12px', background:'#F9FAFB', borderRadius:8, fontSize:11, color:'#6B7280', lineHeight:1.6}}>
                저장 후 백엔드에서 알리고 <code>POST /akv10/template/add/</code> 로 전송되어야 검수가 시작됩니다.
                tpl_code(템플릿 코드)는 검수 통과 후 응답으로 받아 저장됩니다.
              </div>

              <Button theme="primary" block onClick={this.save} disabled={this.isSaving} style={{marginTop: 14, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6}}>
                <FeatherIcon icon="save" size="14"/> {this.isSaving ? '저장 중…' : '템플릿 저장'}
              </Button>
              <Button theme="light" block onClick={this.goBack} disabled={this.isSaving} style={{marginTop: 8}}>
                취소
              </Button>
            </Card>

            {/* 등록된 카카오 채널 리스트 (알리고 /profile/list/) */}
            <Card small style={{padding: '16px 18px', marginTop: 12}}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 12}}>
                <div style={{fontSize: 13, fontWeight: 700, color: '#1F2937', display:'flex', alignItems:'center', gap:8}}>
                  <FeatherIcon icon="message-circle" size="14"/>
                  등록된 카카오 채널
                  <span style={{fontSize: 11, fontWeight: 400, color: '#6B7280'}}>({this.channels.length})</span>
                </div>
                <Button theme="light" size="sm" onClick={this.loadChannels} disabled={this.channelsLoading} style={{padding:'3px 10px', fontSize: 11, display:'inline-flex', alignItems:'center', gap:4}} title="알리고 발신프로필 목록 갱신">
                  <FeatherIcon icon="refresh-cw" size="11"/>
                  {this.channelsLoading ? '갱신 중…' : '갱신'}
                </Button>
              </div>
              {this.channelsError ? (
                <div style={{padding:'10px 12px', background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:6, fontSize:11, color:'#991B1B'}}>
                  {this.channelsError}
                </div>
              ) : this.channels.length === 0 && !this.channelsLoading ? (
                <div style={{padding:'18px 12px', textAlign:'center', fontSize:12, color:'#9CA3AF'}}>
                  조회된 카카오 채널이 없습니다.
                </div>
              ) : (
                <div style={{display:'flex', flexDirection:'column', gap:6, maxHeight: 280, overflowY:'auto'}}>
                  {this.channels.map((ch, i) => {
                    const isFixed = ch.senderkey === this.SENDERKEY;
                    return (
                      <div key={i} style={{
                        display:'flex', alignItems:'center', gap:8, padding:'8px 10px', flexWrap:'wrap',
                        background: isFixed ? '#FFFBEB' : '#F9FAFB',
                        border: isFixed ? '1px solid #FDE68A' : '1px solid #F3F4F6',
                        borderRadius:6
                      }}>
                        <span style={{
                          display:'inline-flex', alignItems:'center', gap:4,
                          padding:'2px 8px', background:'#FEF3C7', color:'#92400E',
                          borderRadius:4, fontSize:11, fontWeight:700, flexShrink:0, maxWidth:180,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'
                        }} title={ch.name || ''}>
                          <FeatherIcon icon="message-circle" size="10"/> {ch.name || '(이름 없음)'}
                        </span>
                        <code style={{fontSize:10, color:'#6B7280', wordBreak:'break-all', fontFamily:'Menlo, Consolas, monospace', flex:1, minWidth:140}}>
                          {ch.senderkey}
                        </code>
                        {ch.status && (() => {
                          const s = (ch.status + '').toUpperCase();
                          const label = s === 'A' ? '정상' : s === 'S' ? '차단' : s === 'D' ? '삭제' : ch.status;
                          const bg = s === 'A' ? '#D1FAE5' : s === 'S' ? '#FEE2E2' : s === 'D' ? '#F3F4F6' : '#E5E7EB';
                          const color = s === 'A' ? '#065F46' : s === 'S' ? '#991B1B' : s === 'D' ? '#6B7280' : '#374151';
                          return (
                            <span title={`상태 (status): ${ch.status}`} style={{fontSize:10, color, fontWeight:600, padding:'1px 6px', background:bg, borderRadius:3, flexShrink:0}}>
                              {label}
                            </span>
                          );
                        })()}
                        {ch.profileStat && (
                          <span title="카카오채널 상태 (profileStat)" style={{fontSize:10, color:'#1E40AF', fontWeight:600, padding:'1px 6px', background:'#DBEAFE', borderRadius:3, flexShrink:0}}>
                            채널: {ch.profileStat}
                          </span>
                        )}
                        {isFixed && (
                          <span style={{fontSize:10, color:'#92400E', fontWeight:700, padding:'1px 6px', background:'#FCD34D', borderRadius:3, flexShrink:0}}>
                            사용중
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default NotificationKakaoTemplate;
